import { DEFAULT_BRAND_COLOR, WCAG_AA, findAccessibleTone } from "./brandPalette";
import { hexToRgb, isHexColor, rgbToHex, rgbToHsl, withHsl, withLightness } from "./contrast";

/** Degraus de `dark_green` no Tailwind; cada um lê `--brand-<degrau>` em canais RGB. */
const STEPS = ["900", "800", "700", "600", "400", "300"] as const;
type Step = (typeof STEPS)[number];

/** Distância de luminosidade de cada degrau ao 800, igual à da rampa institucional. */
const STEP_OFFSET: Record<Step, number> = { 900: -0.05, 800: 0, 700: 0.06, 600: 0.11, 400: 0.3, 300: 0.36 };

/**
 * Superfícies do dark mode (`night*` no Tailwind). Mantêm saturação e luminosidade das da 7Days
 * e giram o matiz o mesmo tanto que a cor do tenant dista da marca padrão, para o fundo não
 * ficar verde com a marca de outra cor.
 */
const NIGHT_SURFACES = { "--night": "#0E1611", "--night-surface": "#16201B", "--night-input": "#1E2C26" } as const;
type NightVariable = keyof typeof NIGHT_SURFACES;

/** O hover do menu clareia o fundo com branco a 10% (`hover:bg-white/10`): é o pior caso do destaque. */
const NAV_HOVER_WHITE = 0.1;

function mixWithWhite(hex: string, amount: number): string {
    const { r, g, b } = hexToRgb(hex);
    const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
    return rgbToHex({ r: mix(r), g: mix(g), b: mix(b) });
}

/**
 * Fundos claros em que a marca é texto ou leva texto: o `white` do Tailwind (texto sobre os
 * degraus escuros) e o fundo claro mais escuro com texto da marca (cabeçalho do Gantt).
 */
const LIGHT_TEXT = "#F7F6F3";
const LIGHT_DARKEST = "#E9E6E1";

/** Degraus que recebem texto branco (`bg-dark_green-*` com `text-white`). */
const FILL_STEPS: readonly Step[] = ["900", "800", "700", "600"];

/** Abaixo disto a cor é tratada como neutra e as superfícies ficam cinza. */
const NEUTRAL_SATURATION = 0.08;

const VARIABLES = [
    ...STEPS.map((step) => `--brand-${step}`),
    "--brand-on-dark",
    ...(Object.keys(NIGHT_SURFACES) as NightVariable[]),
    "--nav-accent",
    "--nav-active-fg",
] as const;

const channels = (hex: string): string => {
    const { r, g, b } = hexToRgb(hex);
    return `${r} ${g} ${b}`;
};

/**
 * Variáveis `--brand-*` a partir da cor do tenant. O 800 (o `dark_green` padrão) é o tom mais
 * próximo da cor que aceita texto branco AA, porque o app inteiro escreve branco sobre ele.
 */
export function buildBrandVariables(color: string): Record<(typeof VARIABLES)[number], string> {
    // O 800 é texto sobre o fundo claro mais escuro; com isso também aceita o texto branco.
    const base = findAccessibleTone(color, LIGHT_DARKEST, WCAG_AA.text);
    const night = buildNightSurfaces(color);
    const nightVariables = Object.fromEntries(
        Object.entries(night).map(([name, hex]) => [name, channels(hex)]),
    ) as Record<NightVariable, string>;
    // O input com o hover por cima é o fundo escuro mais claro: passar nele garante AA em todos.
    const darkWorst = mixWithWhite(night["--night-input"], NAV_HOVER_WHITE);
    const { l } = rgbToHsl(hexToRgb(base));
    const ramp = (step: Step) => withLightness(base, Math.min(0.95, Math.max(0.05, l + STEP_OFFSET[step])));
    const tone = (step: Step): string => {
        const candidate = ramp(step);
        // Degraus de fundo escurecem até o texto branco passar; o 400 é só gráfico (3:1); o 300
        // é texto do dark mode e clareia até passar sobre a pior superfície escura.
        if (FILL_STEPS.includes(step)) return findAccessibleTone(candidate, LIGHT_TEXT, WCAG_AA.text);
        if (step === "400") return findAccessibleTone(candidate, LIGHT_TEXT, WCAG_AA.ui);
        return findAccessibleTone(candidate, darkWorst, WCAG_AA.text);
    };
    const onDark = findAccessibleTone(color, darkWorst, WCAG_AA.text);

    return {
        "--brand-900": channels(tone("900")),
        "--brand-800": channels(base),
        "--brand-700": channels(tone("700")),
        "--brand-600": channels(tone("600")),
        "--brand-400": channels(tone("400")),
        "--brand-300": channels(tone("300")),
        "--brand-on-dark": channels(onDark),
        ...nightVariables,
        "--nav-accent": channels(onDark),
        // O item ativo pinta o fundo com o 800, que já foi escolhido para aceitar branco AA.
        "--nav-active-fg": channels("#FFFFFF"),
    };
}

/** Superfícies night em hex: o menu precisa delas para calcular o destaque. */
function buildNightSurfaces(color: string): Record<NightVariable, string> {
    const { h, s } = rgbToHsl(hexToRgb(color));
    const shift = h - rgbToHsl(hexToRgb(DEFAULT_BRAND_COLOR)).h;
    const tint = (surface: string) => {
        const reference = rgbToHsl(hexToRgb(surface));
        const hue = (((reference.h + shift) % 360) + 360) % 360;
        return withHsl({ h: hue, s: s < NEUTRAL_SATURATION ? 0 : reference.s, l: reference.l });
    };
    return {
        "--night": tint(NIGHT_SURFACES["--night"]),
        "--night-surface": tint(NIGHT_SURFACES["--night-surface"]),
        "--night-input": tint(NIGHT_SURFACES["--night-input"]),
    };
}

/** Sem cor válida volta à marca padrão definida no `globals.css`. */
export function applyBrandTheme(color: string | null, root: HTMLElement = document.documentElement): void {
    if (!color || !isHexColor(color)) {
        VARIABLES.forEach((name) => root.style.removeProperty(name));
        return;
    }
    Object.entries(buildBrandVariables(color)).forEach(([name, value]) => root.style.setProperty(name, value));
}
